import { toast } from "react-toastify";

const MPESA_PAYMENT_METHOD_ID = 4;

const API_BASE_URL = String(
  process.env.REACT_APP_API_MODULE_DOMAIN || ""
).replace(/\/+$/, "");

const BOOKING_ENDPOINT = `${API_BASE_URL}/tickets/booking`;

/**
 * Safely convert values to strings for FormData.
 */
const toStringValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

/**
 * Convert values to a JSON array for the PHP endpoint.
 *
 * Example:
 * ["Jane", "Mary"]
 */
const toJsonArray = (items = []) =>
  JSON.stringify(items.map((item) => toStringValue(item)));

/**
 * Normalize a Kenyan phone number to 254XXXXXXXXX.
 */
const normalizeMpesaPhone = (value) => {
  let phone = toStringValue(value).replace(/\D/g, "");

  if (phone.startsWith("0") && phone.length === 10) {
    phone = `254${phone.substring(1)}`;
  } else if (phone.length === 9) {
    phone = `254${phone}`;
  }

  if (!/^254[17]\d{8}$/.test(phone)) {
    throw new Error(
      "Enter a valid Kenyan M-PESA number, for example 0712345678."
    );
  }

  return phone;
};

/**
 * Get passenger details from the shared passengers array.
 * Falls back to the previous passenger form fields if necessary.
 */
const getPassengers = (allBookingInformation) => {
  const {
    passengers,
    passengerInformation = {},
    contactDetails = {},
    values = {},
  } = allBookingInformation || {};

  if (Array.isArray(passengers) && passengers.length > 0) {
    return passengers.map((passenger, index) => ({
      passenger_index: index,
      passenger_number: index + 1,
      first_name: toStringValue(passenger.first_name),
      last_name: toStringValue(passenger.last_name),
      gender: toStringValue(passenger.gender),
      age: toStringValue(passenger.age),
      id_number: toStringValue(
        passenger.id_number ??
          passenger.renid ??
          passenger.passport_number
      ),
      id_type: toStringValue(passenger.id_type || "Nid"),
      nationality: toStringValue(
        passenger.nationality ?? passenger.country_id
      ),
      mobile: toStringValue(
        passenger.mobile ??
          passenger.contactNo ??
          passenger.phone
      ),
      email: toStringValue(passenger.email),
    }));
  }

  // Legacy fallback for older checkout forms.
  const legacyPassengers = [];

  const firstName =
    passengerInformation.first_name ||
    passengerInformation.firstName ||
    "";

  if (firstName) {
    legacyPassengers.push({
      passenger_index: 0,
      passenger_number: 1,
      first_name: toStringValue(firstName),
      last_name: toStringValue(passengerInformation.last_name),
      gender: toStringValue(passengerInformation.gender),
      age: toStringValue(passengerInformation.age),
      id_number: toStringValue(passengerInformation.id_number),
      id_type: toStringValue(
        passengerInformation.id_type || "Nid"
      ),
      nationality: toStringValue(
        passengerInformation.country_id
      ),
      mobile: toStringValue(
        passengerInformation.mobile ||
          passengerInformation.login_mobile ||
          contactDetails.phone ||
          contactDetails.mobile
      ),
      email: toStringValue(
        contactDetails.email ||
          passengerInformation.login_email
      ),
    });
  }

  const passengerIndexes = new Set();

  Object.keys(values).forEach((key) => {
    const match = key.match(/^(?:first|name)(\d+)$/i);

    if (match) {
      passengerIndexes.add(Number(match[1]));
    }
  });

  [...passengerIndexes]
    .sort((a, b) => a - b)
    .forEach((index) => {
      const suffix = String(index);

      const first =
        values[`first${suffix}`] ||
        values[`name${suffix}`];

      if (!first) return;

      legacyPassengers.push({
        passenger_index: legacyPassengers.length,
        passenger_number: legacyPassengers.length + 1,
        first_name: toStringValue(first),
        last_name: toStringValue(
          values[`second${suffix}`] ||
            values[`surName${suffix}`] ||
            values[`last${suffix}`]
        ),
        gender: toStringValue(values[`gender${suffix}`]),
        age: toStringValue(values[`age${suffix}`]),
        id_number: toStringValue(
          values[`renid${suffix}`] ||
            values[`passPort${suffix}`]
        ),
        id_type: toStringValue(
          values[`id_type${suffix}`] || "Nid"
        ),
        nationality: toStringValue(
          values[`nationality${suffix}`]
        ),
        mobile: toStringValue(
          values[`contactNo${suffix}`] ||
            values[`third${suffix}`] ||
            contactDetails.phone
        ),
        email: toStringValue(
          values[`passengerEmail${suffix}`]
        ),
      });
    });

  return legacyPassengers;
};

/**
 * Append passenger fields in the exact format expected by
 * the supplied PHP journeylist() method.
 *
 * Main passenger:
 *   first_name
 *   last_name
 *   login_mobile
 *   id_number
 *
 * Additional passengers:
 *   first_name_new
 *   last_name_new
 *   login_mobile_new
 *   id_number_new
 *
 * The PHP method reads the additional fields as JSON arrays.
 */
const appendPassengerInformation = (
  bookingData,
  passengers,
  passengerInformation = {},
  contactDetails = {}
) => {
  const mainPassenger = passengers[0] || {};

  if (!mainPassenger.first_name || !mainPassenger.last_name) {
    throw new Error(
      "The first passenger's first name and last name are required."
    );
  }

  if (!mainPassenger.id_number) {
    throw new Error(
      "The first passenger's ID or passport number is required."
    );
  }

  // Preserve a value already supplied by PaymentHelper.
  // Otherwise, use the main passenger's details.
  const existingFirstName = toStringValue(
    bookingData.get("first_name")
  );

  const existingLastName = toStringValue(
    bookingData.get("last_name")
  );

  const contactPhone =
    toStringValue(bookingData.get("login_mobile")) ||
    toStringValue(contactDetails.phone) ||
    toStringValue(contactDetails.mobile) ||
    toStringValue(passengerInformation.login_mobile) ||
    toStringValue(mainPassenger.mobile);

  const contactEmail =
    toStringValue(bookingData.get("login_email")) ||
    toStringValue(contactDetails.email) ||
    toStringValue(passengerInformation.login_email) ||
    toStringValue(mainPassenger.email);

  bookingData.set(
    "first_name",
    existingFirstName ||
      `${mainPassenger.gender || "Mr"} ${mainPassenger.first_name}`.trim()
  );

  bookingData.set(
    "last_name",
    existingLastName || mainPassenger.last_name
  );

  bookingData.set("login_mobile", contactPhone);
  bookingData.set("login_email", contactEmail);

  bookingData.set(
    "id_number",
    toStringValue(bookingData.get("id_number")) ||
      mainPassenger.id_number
  );

  // Do not include passenger 1 in these arrays.
  const additionalPassengers = passengers.slice(1);

  bookingData.set(
    "first_name_new",
    toJsonArray(
      additionalPassengers.map((passenger) => passenger.first_name)
    )
  );

  bookingData.set(
    "last_name_new",
    toJsonArray(
      additionalPassengers.map((passenger) => passenger.last_name)
    )
  );

  bookingData.set(
    "login_mobile_new",
    toJsonArray(
      additionalPassengers.map(
        (passenger) => passenger.mobile || contactPhone
      )
    )
  );

  bookingData.set(
    "id_number_new",
    toJsonArray(
      additionalPassengers.map((passenger) => passenger.id_number)
    )
  );

  // Keep the count for compatibility with other backend code.
  bookingData.set(
    "passenger_count",
    String(passengers.length)
  );

  // Do not send the passengers JSON field as a substitute
  // for the four fields that journeylist() actually reads.
  bookingData.delete("passengers");
};

/**
 * Append trip information to the booking request.
 */
const appendTripInformation = ({
  bookingData,
  trip,
  search,
  isReturn = false,
}) => {
  const suffix = isReturn ? "_round" : "";

  const fields = [
    "aseat",
    "cseat",
    "spseat",
    "vehicle_id",
    "seatnumbers",
    "totalseat",
    "trip_id",
    "subtripId",
  ];

  fields.forEach((field) => {
    bookingData.set(
      `${field}${suffix}`,
      toStringValue(trip?.[field])
    );
  });

  const pickLocation = isReturn
    ? search?.dropLocation
    : search?.pickLocation;

  const dropLocation = isReturn
    ? search?.pickLocation
    : search?.dropLocation;

  bookingData.set(
    `pick_location_id${suffix}`,
    toStringValue(
      pickLocation ??
        trip?.pick_location_id
    )
  );

  bookingData.set(
    `drop_location_id${suffix}`,
    toStringValue(
      dropLocation ??
        trip?.drop_location_id
    )
  );

  bookingData.set(
    `pickstand${suffix}`,
    toStringValue(
      trip?.pickstand ??
        trip?.pick_stand_id
    )
  );

  bookingData.set(
    `dropstand${suffix}`,
    toStringValue(
      trip?.dropstand ??
        trip?.drop_stand_id
    )
  );

  const journeyDate =
    trip?.journeydate ||
    trip?.returnDate ||
    search?.journeydate ||
    search?.returnDate ||
    "";

	if (toStringValue(journeyDate) !== "") {
	  bookingData.set(
		`journeydate${suffix}`,
		toStringValue(journeyDate)
	  );
	}

  const luggageInfo = trip?.luggageInfo || {};

  bookingData.set(
    `paid_max_luggage_pcs${suffix}`,
    String(
      Number(
        trip?.paid_max_luggage_pcs ??
          luggageInfo.paid_max_luggage_pcs
      ) || 0
    )
  );

  bookingData.set(
    `special_max_luggage_pcs${suffix}`,
    String(
      Number(
        trip?.special_max_luggage_pcs ??
          luggageInfo.special_max_luggage_pcs
      ) || 0
    )
  );

  bookingData.set(
    `special_luggage${suffix}`,
    toStringValue(trip?.specialLuggage)
  );
};

/**
 * Clear unused return-trip fields for a one-way booking.
 */
const clearReturnTripInformation = (bookingData) => {
  const returnFields = [
    "aseat",
    "cseat",
    "spseat",
    "vehicle_id",
    "seatnumbers",
    "totalseat",
    "trip_id",
    "subtripId",
    "pick_location_id",
    "drop_location_id",
    "pickstand",
    "dropstand",
    "journeydate",
    "paid_max_luggage_pcs",
    "special_max_luggage_pcs",
    "special_luggage",
  ];

  returnFields.forEach((field) => {
    bookingData.set(`${field}_round`, "");
  });
};

/**
 * Initiate M-PESA STK Push.
 *
 * Important:
 * An accepted STK Push is NOT a confirmed payment.
 * The backend callback must verify the transaction and update
 * the booking payment status.
 */
export const payNow = async (
  bookingData,
  allBookingInformation,
  dispatch,
  history
) => {
  const {
    paymentGateway,
    journeyInfo,
    searchInfo,
    bookingInfo,
    returnFirstJourneyInfo,
    mpesaPhone,
    contactDetails = {},
    passengerInformation = {},
    passengers: suppliedPassengers,
  } = allBookingInformation || {};

  if (!API_BASE_URL) {
    throw new Error(
      "The API domain is missing. Check REACT_APP_API_MODULE_DOMAIN."
    );
  }

  if (Number(paymentGateway) !== MPESA_PAYMENT_METHOD_ID) {
    throw new Error(
      "M-PESA is the only supported payment method."
    );
  }

  if (!bookingData || typeof bookingData.set !== "function") {
    throw new Error("Invalid booking information.");
  }

  if (!bookingInfo || !searchInfo) {
    throw new Error(
      "Trip information is missing. Please select your trip again."
    );
  }

  const isRoundTrip = Boolean(
    journeyInfo?.isRoundTrip ||
      bookingInfo?.isRoundTrip
  );

  const passengers = getPassengers({
    ...allBookingInformation,
    passengers: suppliedPassengers,
  });

  if (!passengers.length) {
    throw new Error(
      "Please enter at least one passenger."
    );
  }

  if (
    passengers.some(
      (passenger) =>
        !passenger.first_name ||
        !passenger.last_name ||
        !passenger.id_number
    )
  ) {
    throw new Error(
      "Every passenger must have a first name, last name, and ID or passport number."
    );
  }

  const enteredMpesaPhone =
    mpesaPhone ||
    contactDetails.mpesaPhone ||
    "";

  const normalizedMpesaPhone =
    normalizeMpesaPhone(enteredMpesaPhone);

  // Add passenger fields in the exact PHP-compatible format.
  appendPassengerInformation(
    bookingData,
    passengers,
    passengerInformation,
    contactDetails
  );

  // M-PESA payment fields.
  bookingData.set("partialpay", "0");
  bookingData.set(
    "pay_method",
    String(MPESA_PAYMENT_METHOD_ID)
  );
  bookingData.set(
    "payment_gateway",
    String(MPESA_PAYMENT_METHOD_ID)
  );
  bookingData.set("mpesa_phone", normalizedMpesaPhone);

  // The backend determines the actual payment status.
  // These values are informational; do not trust them as payment proof.
  bookingData.set("payment_status", "pending");

  // Same passengers are used for both outbound and return legs.
  // Your PHP journeylist() currently reads the same unsuffixed
  // passenger fields for both calls.

  const outboundTrip = isRoundTrip
    ? journeyInfo
    : bookingInfo;

  appendTripInformation({
    bookingData,
    trip: outboundTrip,
    search: searchInfo,
    isReturn: false,
  });

  if (isRoundTrip) {
    const returnTrip = bookingInfo;

    appendTripInformation({
      bookingData,
      trip: returnTrip,
      search: returnFirstJourneyInfo || searchInfo,
      isReturn: true,
    });
  } else {
    clearReturnTripInformation(bookingData);
  }

  try {
    const response = await fetch(BOOKING_ENDPOINT, {
      method: "POST",
      body: bookingData,
    });

    let result;

    try {
      result = await response.json();
    } catch {
      throw new Error(
        "The server returned an invalid payment response."
      );
    }

    // A 202 response or an explicit pending response means
    // the request outcome is not yet confirmed.
    if (
      response.status === 202 ||
      result?.status === "pending"
    ) {
      toast.info(
        "Your M-PESA request is still being processed. Please wait for confirmation before trying again."
      );

      return {
        ...result,
        status: "pending",
        payment_status: result?.payment_status || "pending",
      };
    }

    if (!response.ok || result?.status !== "success") {
      throw new Error(
        result?.message ||
          "Could not initiate M-PESA payment."
      );
    }

    toast.info(
      "M-PESA prompt sent. Check your phone and enter your M-PESA PIN. Your booking will be confirmed after payment verification."
    );

    // STK Push accepted does not mean payment completed.
    return {
      ...result,
      payment_status: result?.payment_status || "pending",
    };
  } catch (error) {
    console.error(
      "M-PESA booking request failed:",
      error
    );

    // Do not automatically retry here. A network failure can occur
    // after the server has received the request.
    throw error;
  }
};